// client/src/components/MerchantGestionStaff.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { firestore } from '../../firebase';
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  setDoc,
  onSnapshot,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import GestionTabs from './GestionTabs';
import MerchantNavbar from './MerchantNavbar';
import emailjs from 'emailjs-com';
import { sanitize } from '../../utils/sanitize';

const MerchantGestionStaff = () => {
  const [user, setUser] = useState(null);

  // Données "entreprise" en mémoire pour éviter des relectures coûteuses.
  const [entrepriseId, setEntrepriseId] = useState(null);
  const [entrepriseData, setEntrepriseData] = useState(null); // contient invitations, base, etc.
  const [invitations, setInvitations] = useState([]);

  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [showStripeModal, setShowStripeModal] = useState(false);

  // Détecter mobile pour troncature + layout
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  );

  const navigate = useNavigate();
  const auth = getAuth();

  // Écouteur media query (mobile/desktop)
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobile(e.matches);
    try {
      mq.addEventListener('change', handler);
    } catch {
      mq.addListener(handler);
    }
    return () => {
      try {
        mq.removeEventListener('change', handler);
      } catch {
        mq.removeListener(handler);
      }
    };
  }, []);

  // Auth + user (live pour rester fonctionnel si le user change son profil)
  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        if (unsubscribeUser) {
          unsubscribeUser();
          unsubscribeUser = null;
        }
        setUser(null);
        setEntrepriseId(null);
        setEntrepriseData(null);
        setInvitations([]);
        navigate('/logingeneral');
        return;
      }

      const userId = currentUser.uid;
      if (!userId) return;

      const userDocRef = doc(firestore, 'users', userId);

      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }

      // On garde un onSnapshot ici pour conserver le comportement live (pseudo, email, etc.)
      unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
        const userData = docSnapshot.data() || {};
        setUser({ ...userData, uid: userId, pseudo: userData?.pseudo });
      });
    });

    return () => {
      if (unsubscribeUser) unsubscribeUser();
      unsubscribeAuth();
    };
  }, [auth, navigate]);

  // Stripe guard + invitations temps réel — via une SEULE subscription sur la collection "entreprises" (limit(1))
  useEffect(() => {
    if (!user?.uid) return;
    const entreprisesCol = collection(firestore, 'entreprises');
    const q = query(entreprisesCol, where('userId', '==', user.uid), limit(1));

    // Une seule subscription : on met à jour entrepriseId, entrepriseData, invitations et showStripeModal
    const unsubscribeEntreprise = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        // Pas encore d’entreprise : on active la modale Stripe et on vide le state.
        setEntrepriseId(null);
        setEntrepriseData(null);
        setInvitations([]);
        setShowStripeModal(true);
        return;
      }

      const docSnap = snapshot.docs[0];
      const data = docSnap.data() || {};
      setEntrepriseId(docSnap.id);
      setEntrepriseData(data);
      setInvitations(Array.isArray(data.invitations) ? data.invitations : []);

      // Garde Stripe depuis la même lecture
      // Si pas de champ "base" ou différent de "active" => modale
      setShowStripeModal(!(data && data.base === 'active'));
    });

    return () => {
      unsubscribeEntreprise();
    };
  }, [user?.uid]);

  // EmailJS
  const sendVerificationEmail = (email) => {
    const templateParams = { to_email: sanitize(email), company: user?.pseudo };
    emailjs
      .send(
        process.env.REACT_APP_EMAIL_SERVICE_ID,
        process.env.REACT_APP_EMAIL_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAIL_USER_ID
      )
      .catch((error) => console.error('Erreur email:', error));
  };

  const sanitizedEmail = useMemo(() => sanitize(employeeEmail || ''), [employeeEmail]);

  const ensureEntrepriseExists = async () => {
    // Crée l’entreprise s’il n’y en a pas encore, retourne l’ID
    if (entrepriseId) return entrepriseId;

    const entreprisesCol = collection(firestore, 'entreprises');
    const newRef = doc(entreprisesCol);
    await setDoc(newRef, {
      pseudo: user?.pseudo || 'Nom non défini',
      createdBy: user?.email,
      email: user?.email,
      userId: user?.uid,
      createdAt: serverTimestamp(),
      invitations: [],
    });
    setEntrepriseId(newRef.id);
    // Pas besoin de setEntrepriseData ici : le onSnapshot se déclenchera.
    return newRef.id;
  };

  const handleInviteEmployee = async () => {
    try {
      const eid = await ensureEntrepriseExists();
      if (!eid) {
        alert("Erreur : entreprise introuvable.");
        return;
      }

      const currentInvits = Array.isArray(entrepriseData?.invitations)
        ? [...entrepriseData.invitations]
        : [];

      // Cherche une invitation existante pour cet email
      const existing = currentInvits.find((inv) => inv.employeeEmail === sanitizedEmail);

      if (existing) {
        if (existing.status === 'accepted') {
          alert('Cet employé a déjà accepté.');
          return;
        }
        if (existing.status === 'pending') {
          alert('Invitation déjà en attente.');
          return;
        }
        if (existing.status === 'rejected') {
          // On remet en pending
          const updated = currentInvits.map((inv) =>
            inv.employeeEmail === sanitizedEmail
              ? { ...inv, status: 'pending', updatedAt: Date.now() }
              : inv
          );
          await updateDoc(doc(firestore, 'entreprises', eid), { invitations: updated });

          // Envoi de mail uniquement si l’utilisateur n’existe pas : côté coûts Firestore,
          // on s’appuie sur l’état et on limite toute re-lecture de "users".
          // Si tu veux absolument vérifier l’existence, garde le flux actuel ailleurs.
          sendVerificationEmail(sanitizedEmail);

          alert('Invitation mise à jour !');
          setEmployeeEmail('');
          setIsAddingEmployee(false);
          return;
        }
      }

      // Ajout simple
      const updated = [
        ...currentInvits,
        {
          employeeEmail: sanitizedEmail,
          status: 'pending',
          invitedBy: user?.email,
          createdAt: Date.now(),
        },
      ];
      await updateDoc(doc(firestore, 'entreprises', eid), { invitations: updated });

      // Envoi email de vérif (on évite une requête "users" dédiée ici pour réduire les lectures)
      sendVerificationEmail(sanitizedEmail);

      alert('Invitation envoyée !');
      setEmployeeEmail('');
      setIsAddingEmployee(false);
    } catch (error) {
      console.error('Erreur invitation :', error);
      alert("Erreur lors de l'invitation.");
    }
  };

  // Helpers mutation sur invitations depuis l’état courant pour éviter des relectures supplémentaires
  const setInvitationStatus = async (targetEmail, newStatus, successMsg) => {
    try {
      if (!entrepriseId) {
        alert('Entreprise non trouvée');
        return;
      }
      const currentInvits = Array.isArray(entrepriseData?.invitations)
        ? [...entrepriseData.invitations]
        : [];

      const idx = currentInvits.findIndex((inv) => inv.employeeEmail === targetEmail);
      if (idx === -1) {
        alert('Invitation introuvable');
        return;
      }
      currentInvits[idx] = {
        ...currentInvits[idx],
        status: newStatus,
        updatedAt: Date.now(),
      };
      await updateDoc(doc(firestore, 'entreprises', entrepriseId), {
        invitations: currentInvits,
      });
      if (successMsg) alert(successMsg);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la mise à jour de l'invitation.");
    }
  };

  // Troncature uniquement mobile (≤640px), 35 max ; desktop : ellipsis CSS
  const formatEmail = (email) => {
    const safe = sanitize(email || '');
    if (!isMobile) return safe;
    return safe.length > 35 ? `${safe.slice(0, 32)}...` : safe; // 32 + "..." ≈ 35
  };

  // Rendu
  return (
    <div>
      <MerchantNavbar />
      <div className="dashboard-container">
        <style>{`
          .mg-scope { width: 100%; min-width: 0; }
          .mg-scope .eb-page-title { margin: 0; text-align: left; }

          /* Onglets de la section Gestion (identiques à MerchantGestion) */
          .mg-scope .tabs-pill { display: inline-flex; gap: 4px; padding: 4px; margin: 0 0 24px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); max-width: 100%; overflow-x: auto; scrollbar-width: none; }
          .mg-scope .tabs-pill::-webkit-scrollbar { display: none; }
          .mg-scope .tab { appearance: none; border: 0; background: transparent; color: var(--eb-text-2); font-weight: 600; font-size: 14px; min-height: 38px; padding: 0 16px; border-radius: var(--eb-radius-sm); cursor: pointer; white-space: nowrap; transition: background-color .15s ease, color .15s ease; }
          .mg-scope .tab:hover { background: var(--eb-surface-2); color: var(--eb-text); }
          .mg-scope .tab-active, .mg-scope .tab-active:hover { background: var(--eb-brand); color: #fff; cursor: default; box-shadow: var(--eb-shadow-xs); }

          .mg-scope .card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 24px; box-shadow: var(--eb-shadow-sm); }
          .mg-scope .ms-section-title { margin: 0 0 4px; font-size: 16px; font-weight: 650; color: var(--eb-text); text-align: left; }
          .mg-scope .ms-section-sub { margin: 0 0 16px; font-size: 13.5px; color: var(--eb-muted); }

          /* Listes */
          .mg-scope .list-clean { list-style: none; padding: 0; margin: 0; border: 1px solid var(--eb-border); border-radius: var(--eb-radius); overflow: hidden; }
          .mg-scope .list-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px; background: var(--eb-surface); }
          .mg-scope .list-item + .list-item { border-top: 1px solid var(--eb-border); }
          .mg-scope .list-item:hover { background: var(--eb-surface-2); }
          .mg-scope .content-row { display: flex; align-items: center; justify-content: space-between; width: 100%; gap: 12px; }
          .mg-scope .email-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14.5px; color: var(--eb-text); font-weight: 500; }
          .mg-scope .actions-col { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
          .mg-scope .ms-list-title { display: flex; align-items: center; gap: 8px; margin: 0 0 10px; font-size: 14px; font-weight: 650; color: var(--eb-text-2); text-align: left; }
          .mg-scope .divider { height: 0; margin: 20px 0 0; }
          .mg-scope .ms-empty { margin: 0; padding: 24px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; border: 1px dashed var(--eb-border-strong); border-radius: var(--eb-radius); background: var(--eb-surface-2); }

          @media (max-width: 768px) {
            .mg-scope .tabs-pill { display: flex; width: 100%; margin-bottom: 20px; }
            .mg-scope .tab { flex: 1 0 auto; padding: 0 12px; min-height: 40px; }
            .mg-scope .card { padding: 18px 16px; }
          }
          @media (max-width: 640px) {
            .mg-scope .content-row { flex-direction: column; align-items: stretch; gap: 10px; }
            .mg-scope .email-text { white-space: normal; overflow: visible; text-overflow: clip; overflow-wrap: anywhere; }
            .mg-scope .actions-col { align-items: stretch; }
            .mg-scope .actions-col button { width: 100%; min-height: 40px; }
          }
        `}</style>

        <div className="mg-scope">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Gestion</h1>
            <p className="eb-page-subtitle">Invitez et gérez les membres de votre équipe</p>
          </div>
        </header>

        {/* Onglets */}
        <GestionTabs active="staff" />

        {showStripeModal ? (
          <div className="secret-key-section card">
            <h2 className="ms-section-title" style={{ fontSize: 18, marginBottom: 12 }}>Activez votre compte entreprise</h2>
            <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--eb-text-2)' }}>
              <li>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
                  Activez votre compte :
                  <button
                    className="button-classic"
                    onClick={() => {
                      const stripeUrl =
                        process.env.NODE_ENV === 'production'
                          ? 'https://buy.stripe.com/fZeg04cUh6Z6b3W144'
                          : 'https://buy.stripe.com/test_00gaI28o69U1adidQQ';
                      window.open(stripeUrl, '_blank');
                    }}
                  >
                    Activer mon compte
                  </button>
                </span>
              </li>
              <li>Activation sous 24-48h après votre paiement</li>
              <li>Profitez pleinement de votre compte entreprise !</li>
            </ol>
          </div>
        ) : (
          <div className="card">
            {/* Invite : bouton en haut à droite, formulaire en modale */}
            <div className="eb-toolbar">
              <div>
                <h2 className="ms-section-title">Équipe</h2>
                <p className="ms-section-sub" style={{ margin: 0 }}>Invitez vos employés par e-mail.</p>
              </div>
              <div className="eb-toolbar__actions">
                <button
                  className="eb-btn eb-btn--primary"
                  onClick={() => {
                    setIsAddingEmployee(true);
                    setEmployeeEmail('');
                  }}
                >
                  + Inviter un employé(e)
                </button>
              </div>
            </div>

            {isAddingEmployee && (
              <div
                className="eb-modal-overlay"
                onClick={(e) => { if (e.target === e.currentTarget) setIsAddingEmployee(false); }}
              >
                <div className="eb-modal" role="dialog" aria-modal="true" aria-labelledby="ms-invite-title">
                  <div className="eb-modal__head">
                    <h2 className="eb-modal__title" id="ms-invite-title">Inviter un employé(e)</h2>
                    <button type="button" className="eb-modal__close" aria-label="Fermer" onClick={() => setIsAddingEmployee(false)}>×</button>
                  </div>
                  <div className="eb-modal__body">
                    <label className="eb-field">
                      <span className="eb-label">Email salarié(e)</span>
                      <input
                        type="email"
                        value={employeeEmail}
                        onChange={(e) => setEmployeeEmail(sanitize(e.target.value))}
                        placeholder="Email salarié(e)"
                        className="eb-input"
                        autoFocus
                      />
                    </label>
                  </div>
                  <div className="eb-modal__foot">
                    <button type="button" className="eb-btn eb-btn--secondary" onClick={() => setIsAddingEmployee(false)}>
                      Annuler
                    </button>
                    <button
                      className="eb-btn eb-btn--primary"
                      onClick={handleInviteEmployee}
                    >
                      Envoyer l'invitation
                    </button>
                  </div>
                </div>
              </div>
            )}

            <hr className="eb-divider" style={{ margin: '4px 0 20px' }} />

            {/* Invitations */}
            {(() => {
              const pending = invitations.filter((inv) => inv.status === 'pending');
              const accepted = invitations.filter((inv) => inv.status === 'accepted');

              const PendingList = () => (
                <div>
                  <h3 className="ms-list-title">
                    Invitations en attente
                    <span className="eb-badge eb-badge--warning">{pending.length}</span>
                  </h3>
                  <ul className="list-clean">
                    {pending.map((invitation, idx) => (
                      <li key={idx} className="list-item">
                        <div className="content-row">
                          <span className="email-text">
                            {formatEmail(invitation.employeeEmail)}
                          </span>
                          <div className="actions-col">
                            <button
                              onClick={async () => {
                                const ok = window.confirm(
                                  `Annuler l'invitation de ${sanitize(
                                    invitation.employeeEmail
                                  )} ?`
                                );
                                if (!ok) return;
                                await setInvitationStatus(
                                  invitation.employeeEmail,
                                  'rejected',
                                  null
                                );
                              }}
                              className="button-classic"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );

              const AcceptedList = () => (
                <div>
                  <h3 className="ms-list-title">
                    Employés affiliés
                    <span className="eb-badge eb-badge--success">{accepted.length}</span>
                  </h3>
                  <ul className="list-clean">
                    {accepted.map((invitation, idx) => (
                      <li key={idx} className="list-item">
                        <div className="content-row">
                          <span className="email-text">
                            {formatEmail(invitation.employeeEmail)}
                          </span>
                          <div className="actions-col">
                            <button
                              className="button-delete"
                              onClick={async () => {
                                await setInvitationStatus(
                                  invitation.employeeEmail,
                                  'rejected',
                                  `L'employé(e) ${sanitize(
                                    invitation.employeeEmail
                                  )} a été désaffilié(e).`
                                );
                              }}
                            >
                              Désaffilier
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );

              return (
                <div>
                  {pending.length > 0 && <PendingList />}
                  {pending.length > 0 && accepted.length > 0 && (
                    <div className="divider" />
                  )}
                  {accepted.length > 0 && <AcceptedList />}

                  {pending.length === 0 && accepted.length === 0 && (
                    <p className="ms-empty">
                      Aucune invitation pour le moment.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MerchantGestionStaff;
